import type { LoaderFunctionArgs, MetaFunction } from "react-router";
import { useLoaderData } from "react-router";
import { SITE_NAME, API_BASE } from "@/lib/constants";
import { safeFetchJson } from "@/lib/fetch-json";
import type { TickerResult } from "@/lib/api";
import TickerPage from "@/pages/TickerPage";

export async function loader({ params }: LoaderFunctionArgs) {
  const symbol = params.symbol!.toUpperCase();
  const ticker = await safeFetchJson<TickerResult>(`${API_BASE}/ticker/${symbol}`, {
    timeoutMs: 3000,
  });
  return { symbol, ticker };
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const symbol = data?.symbol ?? "";
  const ticker = data?.ticker;
  const origin = "https://loadlogo.com";

  if (ticker && !ticker.error) {
    const name = ticker.name || symbol;
    const description = `Download the ${name} (${symbol}) stock ticker logo. ${ticker.exchange ? `Listed on ${ticker.exchange}.` : ""} Get logos via the ${SITE_NAME} API.`;
    return [
      { title: `${symbol} Stock Logo | ${name} | ${SITE_NAME}` },
      { name: "description", content: description },
      { property: "og:title", content: `${symbol} Stock Logo | ${name} | ${SITE_NAME}` },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:image", content: `${origin}/og/ticker/${symbol}` },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:url", content: `${origin}/ticker/${symbol}` },
      { property: "og:site_name", content: SITE_NAME },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: `${symbol} Stock Logo | ${name} | ${SITE_NAME}` },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: `${origin}/og/ticker/${symbol}` },
      { tagName: "link", rel: "canonical", href: `${origin}/ticker/${symbol}` },
    ];
  }

  return [
    { title: `${symbol} Stock Logo | ${SITE_NAME}` },
    {
      name: "description",
      content: `Download the ${symbol} stock ticker logo via the ${SITE_NAME} API.`,
    },
    { property: "og:title", content: `${symbol} Stock Logo | ${SITE_NAME}` },
    {
      property: "og:description",
      content: `Download the ${symbol} stock ticker logo via the ${SITE_NAME} API.`,
    },
    { property: "og:type", content: "website" },
    { property: "og:image", content: `${origin}/og/ticker/${symbol}` },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:url", content: `${origin}/ticker/${symbol}` },
    { property: "og:site_name", content: SITE_NAME },
    { name: "twitter:card", content: "summary_large_image" },
    { tagName: "link", rel: "canonical", href: `${origin}/ticker/${symbol}` },
  ];
};

export default function TickerRoute() {
  const { ticker } = useLoaderData<typeof loader>();
  return <TickerPage serverData={ticker} />;
}
